import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Badge,
  HStack,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import axios from "../../axiosConfig";
import ChecklistItemForm from "./ChecklistItemForm";

const ChecklistItemsList = ({ widgetId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchItems = async () => {
    try {
      const res = await axios.get(`/widgets/${widgetId}/checklist_items`);
      setItems(res.data);
    } catch (err) {
      console.error("Error fetching checklist items:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [widgetId]);

   const existingCategories = useMemo(() => {
      const set = new Set(
        (items || [])
          .map((i) => (i.category || "").trim())
          .filter((c) => c.length > 0)
      );
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [items]);

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`/widgets/${widgetId}/checklist_items/${itemId}`);
      fetchItems();
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await axios.put(`/widgets/${widgetId}/checklist_items/${editingItem.id}`, {
          checklist_item: data, // includes label, position, category
        });
      } else {
        await axios.post(`/widgets/${widgetId}/checklist_items`, {
          checklist_item: data, // includes label, position, category
        });
      }
      fetchItems();
      onClose();
      setEditingItem(null);
    } catch (err) {
      console.error("Error saving item:", err);
    }
  };

  // Client-side sort: by category (alpha, empty last), then position, then label
  const sortedItems = useMemo(() => {
    const norm = (v) => (v ?? "").toString().trim().toLowerCase();
    return [...items].sort((a, b) => {
      const ac = norm(a.category);
      const bc = norm(b.category);
      if (ac === "" && bc !== "") return 1;
      if (ac !== "" && bc === "") return -1;
      if (ac !== bc) return ac.localeCompare(bc);
      const ap = a.position ?? Number.MAX_SAFE_INTEGER;
      const bp = b.position ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
      return norm(a.label).localeCompare(norm(b.label));
    });
  }, [items]);

  if (loading) return <Spinner size="lg" />;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Checklist Items</Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="orange"
          onClick={() => {
            setEditingItem(null);
            onOpen();
          }}
        >
          Add Item
        </Button>
      </Flex>

      {sortedItems.length === 0 ? (
        <Text>No checklist items defined yet.</Text>
      ) : (
        sortedItems.map((item) => (
          <Flex
            key={item.id}
            justify="space-between"
            align="center"
            p={3}
            mb={2}
            borderWidth="1px"
            borderRadius="md"
            bg="white"
          >
            <Box>
              <Text fontWeight="medium">{item.label}</Text>
              <HStack spacing={2} mt={1}>
                <Badge colorScheme="purple" variant="subtle">
                  {item.category?.trim() || "General"}
                </Badge>
                {typeof item.position === "number" && (
                  <Badge colorScheme="gray" variant="outline">pos: {item.position}</Badge>
                )}
              </HStack>
            </Box>

            <Flex gap={2}>
              <IconButton
                icon={<EditIcon />}
                aria-label="Edit item"
                size="sm"
                onClick={() => {
                  setEditingItem(item);
                  onOpen();
                }}
              />
              <IconButton
                icon={<DeleteIcon />}
                aria-label="Delete item"
                size="sm"
                colorScheme="red"
                onClick={() => handleDelete(item.id)}
              />
            </Flex>
          </Flex>
        ))
      )}

      {/* Modal for Add/Edit */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingItem ? "Edit Checklist Item" : "Add Checklist Item"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChecklistItemForm
              initialData={editingItem}
              onSubmit={handleSave}
              onCancel={() => {
                setEditingItem(null);
                onClose();
              }}
              existingCategories={existingCategories}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChecklistItemsList;